import React, { useEffect, useRef, useState } from "react";
import LoyaltyCard from "./LoyaltyCard";
import SidebarMenu from "containers/UserDashboard/SideBarMenu";
import { motion } from "framer-motion";
import { FiPlusCircle, FiRefreshCw, FiCheckCircle } from "react-icons/fi";
// import { sampleCards } from "./data/SampleLoyallyCards";
import { useLocation } from "react-router-dom";
import Loading from "components/Loading/Loading";
import { HiOutlineSparkles } from "react-icons/hi";
import { BiBadge } from "react-icons/bi";
import { TbSparkles } from "react-icons/tb";
import userApi from "api/user/user.api";
import operatorApi from "api/operator/operator.api";
import { useDispatch, useSelector } from "react-redux";
import { setAuthModalOpen } from "../../redux/features/authModalSlice";
import loyaltyApi from "api/loyalty/loyaltyApi";

const CARDS_PER_PAGE = 9;

export default function LoyaltyWallets() {
  const [cardDesign, setCardDesign] = useState([]);
  const [operators, setOperators] = useState([]);
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();
  const location = useLocation();

  const initialTab = location.state?.status || "new";

  // Fetch Operators
  const getOperatorList = async () => {
    try {
      const response = await operatorApi.getListOperator(user?._id);
      if (response.success && response.operators) {
        setOperators(response.operators);
        return response.operators;
      }
      return [];
    } catch (error) {
      console.error("Error fetching operators:", error);
      return [];
    }
  };

  const getRoyaltyCard = async (operatorList) => {
    try {
      const response = await userApi.getListCardDesign(user?._id);
      if (response.success && response.stamps) {
        const enrichedCards = await Promise.all(
          response.stamps.map(async (card) => {
            const stampAwardNumberVariables = {
              userFrom: card.writer,
              userTo: user._id,
            };

            let stampCount = 0;
            try {
              const { response, err } = await loyaltyApi.getStampAwardNumber(
                stampAwardNumberVariables
              );
              if (response?.data?.success) {
                stampCount = response.data.stampAwardNumber;
              }
            } catch (err) {
              console.error("Failed to fetch stamp count for card", card._id);
            }

            const stampsselected = card.NoofStamps;
            const createdDate = new Date(card.createdAt);
            const now = new Date();
            const daysSinceCreated =
              (now - createdDate) / (1000 * 60 * 60 * 24);

            let status = "ongoing";
            if (stampCount >= stampsselected) status = "completed";
            else if (daysSinceCreated <= 7) status = "new";

            const operator = operatorList.find((op) => op._id === card.writer);

            return {
              ...card,
              stampCount,
              stampsselected,
              status,
              operatorPhoto: operator?.photo || null,
              operatorCover: operator?.coverPhoto || null,
              operatorName: operator?.name || "",
              type: "Loyalty",
            };
          })
        );

        setCardDesign(enrichedCards);

        // Set initial tab logic...
        const statusCounts = {
          new: enrichedCards.filter((card) => card.status === "new").length,
          ongoing: enrichedCards.filter((card) => card.status === "ongoing")
            .length,
          completed: enrichedCards.filter((card) => card.status === "completed")
            .length,
        };

        let decidedTab = "new";
        if (statusCounts.new === 0 && statusCounts.ongoing > 0) {
          decidedTab = "ongoing";
        }
        if (statusCounts.new === 0 && statusCounts.ongoing === 0) {
          decidedTab = "completed";
        }
        if (location.state?.status) {
          decidedTab = location.state.status;
        }

        setTabValue(decidedTab);
      }
    } catch (error) {
      console.error("Error fetching card design:", error);
    } finally {
      setLoading(false);
    }
  };

  // console.log({ cardDesign });

  // console.log({ operators });

  useEffect(() => {
    const fetchData = async () => {
      if (!user) {
        dispatch(setAuthModalOpen(true));
        return setLoading(false);
      }

      try {
        const operatorList = await getOperatorList(); // ✅ ensure we get actual data
        await getRoyaltyCard(operatorList); // ✅ pass operators explicitly
      } catch (error) {
        console.error("Failed to load data", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const sampleCards = cardDesign;

  const [tabValue, setTabValue] = useState(initialTab);
  const [desktopPage, setDesktopPage] = useState(1);
  const [mobilePage, setMobilePage] = useState(1);
  const [loading, setLoading] = useState(true);

  const mobileScrollRef = useRef(null);
  const desktopScrollRef = useRef(null);
  const lastCardRef = useRef(null);
  const firstCardRef = useRef(null);

  const filteredCards = sampleCards.filter((card) => card.status === tabValue);
  const totalPages = Math.ceil(filteredCards.length / CARDS_PER_PAGE);

  const visibleMobileCards = filteredCards.slice(
    0,
    mobilePage * CARDS_PER_PAGE
  );
  const visibleDesktopCards = filteredCards.slice(
    0,
    desktopPage * CARDS_PER_PAGE
  );

  const handleTabChange = (value) => {
    setTabValue(value);
    setDesktopPage(1);
    setMobilePage(1);
    if (mobileScrollRef.current) mobileScrollRef.current.scrollTop = 0;
  };

  // 📱 Infinite scroll for mobile
  useEffect(() => {
    const container = mobileScrollRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (
        scrollTop + clientHeight >= scrollHeight - 100 &&
        mobilePage < totalPages
      ) {
        setMobilePage((prev) => prev + 1);
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [mobilePage, totalPages]);

  // 💻 Infinite scroll for desktop (scroll up + down)
  useEffect(() => {
    if (!desktopScrollRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.getAttribute("data-id");
          if (entry.isIntersecting) {
            if (id === "last" && desktopPage < totalPages) {
              setDesktopPage((prev) => prev + 1);
            }
            if (id === "first" && desktopPage > 1) {
              setDesktopPage((prev) => prev - 1);
            }
          }
        });
      },
      { root: desktopScrollRef.current, threshold: 1.0 }
    );

    if (lastCardRef.current) observer.observe(lastCardRef.current);
    if (firstCardRef.current) observer.observe(firstCardRef.current);

    return () => {
      if (lastCardRef.current) observer.unobserve(lastCardRef.current);
      if (firstCardRef.current) observer.unobserve(firstCardRef.current);
    };
  }, [visibleDesktopCards, desktopPage, totalPages]);

  const isOneStampFromBonus = (card) => {
    const getBonusIndexes = (stampsselected) => {
      if (stampsselected <= 5) return [stampsselected - 1];
      const numBonuses =
        stampsselected <= 10 ? 2 : stampsselected <= 15 ? 3 : 4;
      return Array.from(
        { length: numBonuses },
        (_, i) => Math.round(((i + 1) / numBonuses) * stampsselected) - 1
      );
    };

    const bonusIndexes = getBonusIndexes(card.stampsselected);
    return bonusIndexes.includes(card.stampCount);
  };

  const tabs = [
    { value: "new", icon: HiOutlineSparkles, color: "text-green-600" },
    { value: "ongoing", icon: FiRefreshCw, color: "text-yellow-400" },
    { value: "completed", icon: FiCheckCircle, color: "text-green-600" },
  ];

  const currentTab = tabs.find((tab) => tab.value === tabValue);
  const tabCounts = tabs.reduce((acc, tab) => {
    acc[tab.value] = sampleCards.filter(
      (card) => card.status === tab.value
    ).length;
    return acc;
  }, {});

  const bonusCounts = tabs.reduce((acc, tab) => {
    acc[tab.value] = sampleCards.filter(
      (card) => card.status === tab.value && isOneStampFromBonus(card)
    ).length;
    return acc;
  }, {});

  const totalBonusSoonCards = sampleCards.filter(isOneStampFromBonus).length;

  return (
    <div className="bg-gray-900 text-white min-h-screen pb-8 pt-12 px-4">
      <div className="container mx-auto px-2">
        <div className="xl:grid grid-cols-8 gap-10 items-start md:py-12 py-0">
          <div className="hidden md:block sticky top-16 z-30 col-span-2 bg-gray-800 p-6 rounded-md xl:mb-0 mb-5 max-h-[calc(100vh-4rem)] overflow-y-auto">
            <SidebarMenu />
          </div>

          <div className="col-span-6 rounded-md p-0 md:p-6">
            {loading ? (
              <div className="flex items-center justify-center h-[400px] text-lg font-semibold animate-pulse">
                <Loading />
              </div>
            ) : (
              <div className="text-white">
                {/* Header + Tabs */}
                <div className="sticky top-16 z-10 pt-6 bg-gray-900">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h1 className="text-3xl font-bold">Wallet</h1>
                      <p className={`text-sm mt-1 ${currentTab.color}`}>
                        {currentTab.value.charAt(0).toUpperCase() +
                          currentTab.value.slice(1)}
                      </p>
                    </div>
                    <div className="text-right text-sm text-gray-300">
                      <p>Cards: {sampleCards.length}</p>
                      {totalBonusSoonCards > 0 && (
                        <p>
                          Going bonus:{" "}
                          <span className="text-redlight">
                            {totalBonusSoonCards}
                          </span>
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-4 border-b border-gray-700 mb-6">
                    {tabs.map((tab) => {
                      const Icon = tab.icon;
                      const isActive = tabValue === tab.value;
                      return (
                        <button
                          key={tab.value}
                          onClick={() => handleTabChange(tab.value)}
                          className={`px-4 py-2 rounded-t-md text-xl transition-all duration-200 ${
                            isActive ? "bg-gray-700" : "hover:text-white"
                          }`}
                        >
                          <div className="flex items-center gap-1">
                            <Icon
                              className={`${
                                isActive ? tab.color : "text-gray-400"
                              }`}
                              size={22}
                            />
                            <span
                              className={`text-xs ${
                                isActive ? tab.color : "text-gray-400"
                              }`}
                            >
                              ({tabCounts[tab.value]})
                              {bonusCounts[tab.value] > 0 && (
                                <span className="ml-1 text-redlight">
                                  ({bonusCounts[tab.value]}★)
                                </span>
                              )}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* 📱 Mobile View */}
                <motion.div
                  ref={mobileScrollRef}
                  className="block lg:hidden relative max-w-sm mx-auto h-[500px] overflow-y-auto pr-2"
                  initial="hidden"
                  animate="show"
                  variants={{ show: { transition: { staggerChildren: 0.05 } } }}
                >
                  {visibleMobileCards.map((card, index) => (
                    <motion.div
                      key={card.id}
                      variants={{
                        hidden: { opacity: 0, y: 30 },
                        show: { opacity: 1, y: 0 },
                      }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <LoyaltyCard card={card} index={index} />
                    </motion.div>
                  ))}
                </motion.div>

                {/* 💻 Desktop View */}
                <motion.div
                  ref={desktopScrollRef}
                  className="hidden lg:grid grid-cols-2 xl:grid-cols-3 max-h-[600px] gap-x-3 overflow-y-auto pr-2 scrollbar-thin"
                  initial="hidden"
                  animate="show"
                  variants={{ show: { transition: { staggerChildren: 0.05 } } }}
                >
                  {visibleDesktopCards.map((card, index) => {
                    const isFirst = index === 0;
                    const isLast = index === visibleDesktopCards.length - 1;
                    return (
                      <motion.div
                        key={card.id}
                        ref={
                          isLast ? lastCardRef : isFirst ? firstCardRef : null
                        }
                        data-id={isLast ? "last" : isFirst ? "first" : ""}
                        variants={{
                          hidden: { opacity: 0, y: 20 },
                          show: { opacity: 1, y: 0 },
                        }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <LoyaltyCard card={card} index={index} />
                      </motion.div>
                    );
                  })}
                </motion.div>

                {/* Desktop Pagination Buttons */}
                <div className="hidden lg:flex justify-center mt-6 items-center space-x-2">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (page) => (
                      <button
                        key={page}
                        onClick={() => setDesktopPage(page)}
                        className={`px-3 py-1 rounded-full font-semibold transition-all ${
                          page === desktopPage
                            ? "text-redlight border border-redlight"
                            : "text-gray-400 hover:text-white"
                        }`}
                      >
                        {page}
                      </button>
                    )
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// import React, { useEffect, useRef, useState } from "react";
// import LoyaltyCard from "./LoyaltyCard";
// import SidebarMenu from "containers/UserDashboard/SideBarMenu";
// import { motion } from "framer-motion";
// import { FiPlusCircle, FiRefreshCw, FiCheckCircle } from "react-icons/fi";
// import { sampleCards } from "./data/SampleLoyallyCards";
// import { useLocation } from "react-router-dom";
// import Loading from "components/Loading/Loading";

// const CARDS_PER_PAGE = 9;

// export default function LoyaltyWallet() {
//   const location = useLocation();
//   const initialTab = location.state?.status || "new";

//   const [tabValue, setTabValue] = useState(initialTab);
//   const [desktopPage, setDesktopPage] = useState(1);
//   const [mobilePage, setMobilePage] = useState(1);
//   const [loading, setLoading] = useState(true);

//   const mobileScrollRef = useRef(null);
//   const lastCardRef = useRef(null);

//   const filteredCards = sampleCards.filter((card) => card.status === tabValue);
//   const totalPages = Math.ceil(filteredCards.length / CARDS_PER_PAGE);

//   const visibleMobileCards = filteredCards.slice(
//     0,
//     mobilePage * CARDS_PER_PAGE
//   );
//   const visibleDesktopCards = filteredCards.slice(
//     0,
//     desktopPage * CARDS_PER_PAGE
//   );

//   const handleTabChange = (value) => {
//     setTabValue(value);
//     setDesktopPage(1);
//     setMobilePage(1);
//     if (mobileScrollRef.current) mobileScrollRef.current.scrollTop = 0;
//   };

//   useEffect(() => {
//     const timer = setTimeout(() => setLoading(false), 2000);
//     return () => clearTimeout(timer);
//   }, []);

//   // 📱 Infinite scroll for mobile
//   useEffect(() => {
//     const container = mobileScrollRef.current;
//     if (!container) return;

//     const handleScroll = () => {
//       const { scrollTop, scrollHeight, clientHeight } = container;
//       if (
//         scrollTop + clientHeight >= scrollHeight - 100 &&
//         mobilePage < totalPages
//       ) {
//         setMobilePage((prev) => prev + 1);
//       }
//     };

//     container.addEventListener("scroll", handleScroll);
//     return () => container.removeEventListener("scroll", handleScroll);
//   }, [mobilePage, totalPages]);
//   const desktopScrollRef = useRef(null); // add this

//   // 💻 Infinite scroll for desktop
//   useEffect(() => {
//     if (typeof window === "undefined") return;

//     const observer = new IntersectionObserver(
//       ([entry]) => {
//         if (entry.isIntersecting && desktopPage < totalPages) {
//           setDesktopPage((prev) => prev + 1);
//         }
//       },
//       { root: null, threshold: 1.0 }
//     );

//     const target = lastCardRef.current;
//     if (target) observer.observe(target);

//     return () => {
//       if (target) observer.unobserve(target);
//     };
//   }, [visibleDesktopCards, desktopPage, totalPages]);

//   const tabs = [
//     { value: "new", icon: FiPlusCircle, color: "text-white" },
//     { value: "ongoing", icon: FiRefreshCw, color: "text-yellow-400" },
//     { value: "completed", icon: FiCheckCircle, color: "text-green-600" },
//   ];

//   const currentTab = tabs.find((tab) => tab.value === tabValue);
//   const tabCounts = tabs.reduce((acc, tab) => {
//     acc[tab.value] = sampleCards.filter(
//       (card) => card.status === tab.value
//     ).length;
//     return acc;
//   }, {});

//   return (
//     <div className="bg-gray-900 text-white min-h-screen pb-8 pt-12 px-4">
//       <div className="container mx-auto px-2">
//         <div className="xl:grid grid-cols-8 gap-10 items-start md:py-12 py-0">
//           <div className="hidden md:block sticky top-16 z-30 col-span-2 bg-gray-800 p-6 rounded-md xl:mb-0 mb-5 max-h-[calc(100vh-4rem)] overflow-y-auto">
//             <SidebarMenu />
//           </div>

//           <div className="col-span-6 rounded-md p-0 md:p-6">
//             {loading ? (
//               <div className="flex items-center justify-center h-[400px] text-lg font-semibold animate-pulse">
//                 <Loading />
//               </div>
//             ) : (
//               <div className="text-white">
//                 {/* Header + Tabs */}
//                 <div className="sticky top-16 z-10 pt-6 bg-gray-900">
//                   <div className="flex items-center justify-between mb-6">
//                     <div>
//                       <h1 className="text-3xl font-bold">Wallet</h1>
//                       <p className={`text-sm mt-1 ${currentTab.color}`}>
//                         {currentTab.value.charAt(0).toUpperCase() +
//                           currentTab.value.slice(1)}
//                       </p>
//                     </div>
//                     <span className="text-sm text-gray-300">
//                       Cards: {sampleCards.length}
//                     </span>
//                   </div>

//                   <div className="flex gap-4 border-b border-gray-700 mb-6">
//                     {tabs.map((tab) => {
//                       const Icon = tab.icon;
//                       const isActive = tabValue === tab.value;
//                       return (
//                         <button
//                           key={tab.value}
//                           onClick={() => handleTabChange(tab.value)}
//                           className={`px-4 py-2 rounded-t-md text-xl transition-all duration-200 ${
//                             isActive ? "bg-gray-700" : "hover:text-white"
//                           }`}
//                         >
//                           <div className="flex items-center gap-1">
//                             <Icon
//                               className={`${
//                                 isActive ? tab.color : "text-gray-400"
//                               }`}
//                               size={22}
//                             />
//                             <span
//                               className={`text-xs ${
//                                 isActive ? tab.color : "text-gray-400"
//                               }`}
//                             >
//                               ({tabCounts[tab.value]})
//                             </span>
//                           </div>
//                         </button>
//                       );
//                     })}
//                   </div>
//                 </div>

//                 {/* 📱 Mobile View */}
//                 <motion.div
//                   ref={mobileScrollRef}
//                   className="block lg:hidden relative max-w-sm mx-auto h-[500px] overflow-y-auto pr-2"
//                   initial="hidden"
//                   animate="show"
//                   variants={{ show: { transition: { staggerChildren: 0.05 } } }}
//                 >
//                   {visibleMobileCards.map((card, index) => (
//                     <motion.div
//                       key={card.id}
//                       variants={{
//                         hidden: { opacity: 0, y: 30 },
//                         show: { opacity: 1, y: 0 },
//                       }}
//                       transition={{ delay: index * 0.05 }}
//                     >
//                       <LoyaltyCard card={card} index={index} />
//                     </motion.div>
//                   ))}
//                 </motion.div>

//                 {/* 💻 Desktop View */}
//                 <motion.div
//                   ref={desktopScrollRef}
//                   className="hidden lg:grid grid-cols-2 xl:grid-cols-3 gap-6 max-h-[720px] overflow-y-auto pr-2 scrollbar-thin"
//                   initial="hidden"
//                   animate="show"
//                   variants={{ show: { transition: { staggerChildren: 0.05 } } }}
//                 >
//                   {visibleDesktopCards.map((card, index) => {
//                     const isLast = index === visibleDesktopCards.length - 1;
//                     return (
//                       <motion.div
//                         key={card.id}
//                         ref={isLast ? lastCardRef : null}
//                         variants={{
//                           hidden: { opacity: 0, y: 20 },
//                           show: { opacity: 1, y: 0 },
//                         }}
//                         transition={{ delay: index * 0.1 }}
//                       >
//                         <LoyaltyCard card={card} index={index} />
//                       </motion.div>
//                     );
//                   })}
//                 </motion.div>
//                 <div className="hidden lg:flex justify-center mt-6 items-center space-x-2">
//                   {Array.from({ length: totalPages }, (_, i) => i + 1).map(
//                     (page) => (
//                       <button
//                         key={page}
//                         onClick={() => setDesktopPage(page)}
//                         className={`px-3 py-1 rounded-full font-semibold transition-all ${
//                           page === desktopPage
//                             ? "text-redlight border border-redlight"
//                             : "text-gray-400 hover:text-white"
//                         }`}
//                       >
//                         {page}
//                       </button>
//                     )
//                   )}
//                 </div>
//               </div>
//             )}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// }

// // src/pages/LoyaltyWallet.js

// import React, { useEffect, useRef, useState } from "react";
// import LoyaltyCard from "./LoyaltyCard";
// import SidebarMenu from "containers/UserDashboard/SideBarMenu";
// import { motion } from "framer-motion";
// import { FiPlusCircle, FiRefreshCw, FiCheckCircle } from "react-icons/fi";
// import { sampleCards } from "./data/SampleLoyallyCards";
// import { useLocation } from "react-router-dom";
// import Loading from "components/Loading/Loading";

// const CARDS_PER_PAGE = 9;

// export default function LoyaltyWallet() {
//   const scrollRef = useRef(null);
//   const [currentPage, setCurrentPage] = useState(1);
//   const location = useLocation();
//   const initialTab = location.state?.status || "new";
//   const [tabValue, setTabValue] = useState(initialTab);

//   const handlePageChange = (page) => setCurrentPage(page);
//   const handleTabChange = (value) => {
//     setTabValue(value);
//     setCurrentPage(1);
//     if (scrollRef.current) scrollRef.current.scrollTop = 0;
//   };

//   const filteredCards = sampleCards.filter((card) => card.status === tabValue);
//   const totalPages = Math.ceil(filteredCards.length / CARDS_PER_PAGE);
//   const visibleCards = filteredCards.slice(0, currentPage * CARDS_PER_PAGE);

//   useEffect(() => {
//     const container = scrollRef.current;
//     if (!container) return;

//     const handleScroll = () => {
//       const scrollTop = container.scrollTop;
//       const scrollHeight = container.scrollHeight;
//       const clientHeight = container.clientHeight;

//       if (
//         scrollTop + clientHeight >= scrollHeight - 100 &&
//         currentPage < totalPages
//       ) {
//         setCurrentPage((prev) => prev + 1);
//       }
//     };

//     container.addEventListener("scroll", handleScroll);
//     return () => container.removeEventListener("scroll", handleScroll);
//   }, [currentPage, totalPages]);

//   const paginatedCards = filteredCards.slice(
//     (currentPage - 1) * CARDS_PER_PAGE,
//     currentPage * CARDS_PER_PAGE
//   );

//   const tabs = [
//     {
//       value: "new",
//       icon: FiPlusCircle,
//       color: "text-white",
//     },
//     {
//       value: "ongoing",
//       icon: FiRefreshCw,
//       color: "text-yellow-400",
//     },
//     {
//       value: "completed",
//       icon: FiCheckCircle,
//       color: "text-green-600",
//     },
//   ];

//   const currentTab = tabs.find((tab) => tab.value === tabValue);
//   const tabCounts = tabs.reduce((acc, tab) => {
//     acc[tab.value] = sampleCards.filter(
//       (card) => card.status === tab.value
//     ).length;
//     return acc;
//   }, {});

//   const [loading, setLoading] = useState(true); // ⏳ loading state
//   useEffect(() => {
//     const timer = setTimeout(() => {
//       setLoading(false);
//     }, 2000); // 2 second delay
//     return () => clearTimeout(timer);
//   }, []);

//   return (
//     <div className="bg-gray-900 text-white min-h-screen pb-8 pt-12 px-4">
//       <div className="container mx-auto px-2">
//         <div className="xl:grid grid-cols-8 gap-10 items-start md:py-12 py-0 ">
//           <div className="hidden md:block sticky top-16 z-30 col-span-2 bg-gray-800 p-6 rounded-md xl:mb-0 mb-5 max-h-[calc(100vh-4rem)] overflow-y-auto">
//             <SidebarMenu />
//           </div>

//           <div className="col-span-6 rounded-md  p-0 md:p-6">
//             {loading ? (
//               <div className="flex items-center justify-center h-[400px] text-lg font-semibold animate-pulse">
//                 <Loading />
//               </div>
//             ) : (
//               <div className="text-white">
//                 <div className="sticky top-16 z-10 pt-6 bg-gray-900 ">
//                   <div className="flex items-center justify-between mb-6">
//                     <div>
//                       <h1 className="text-3xl font-bold">Wallet</h1>
//                       <p className={`text-sm mt-1 ${currentTab.color}`}>
//                         {currentTab.value.charAt(0).toUpperCase() +
//                           currentTab.value.slice(1)}
//                       </p>
//                     </div>

//                     <span className="text-sm text-gray-300">
//                       Cards: {sampleCards.length}
//                     </span>
//                   </div>

//                   <div className="flex gap-4 border-b border-gray-700 mb-6">
//                     {tabs.map((tab) => {
//                       const Icon = tab.icon;
//                       const isActive = tabValue === tab.value;
//                       return (
//                         <button
//                           key={tab.value}
//                           onClick={() => handleTabChange(tab.value)}
//                           className={`px-4 py-2 rounded-t-md transition-all duration-200 text-xl
//                           ${isActive ? "bg-gray-700" : "hover:text-white"}`}
//                         >
//                           <div className="flex items-center gap-1">
//                             <Icon
//                               className={`$ {
//                               isActive ? tab.color : "text-gray-400"
//                             }`}
//                               size={22}
//                             />
//                             <span
//                               className={`text-xs $ {
//                               isActive ? tab.color : "text-gray-400"
//                             }`}
//                             >
//                               ({tabCounts[tab.value]})
//                             </span>
//                           </div>
//                         </button>
//                       );
//                     })}
//                   </div>
//                 </div>
//                 {/* mobile view  */}
//                 <motion.div
//                   ref={scrollRef}
//                   className="block lg:hidden relative max-w-sm mx-auto h-[500px] overflow-y-auto pr-2"
//                   initial="hidden"
//                   animate="show"
//                   variants={{ show: { transition: { staggerChildren: 0.05 } } }}
//                 >
//                   {filteredCards
//                     .slice(0, currentPage * CARDS_PER_PAGE)
//                     .map((card, index) => (
//                       <motion.div
//                         key={card.id}
//                         variants={{
//                           hidden: { opacity: 0, y: 30 },
//                           show: { opacity: 1, y: 0 },
//                         }}
//                         transition={{ delay: index * 0.05 }}
//                       >
//                         <LoyaltyCard card={card} index={index} />
//                       </motion.div>
//                     ))}
//                 </motion.div>

//                 {/* desktop view  */}

//                 <motion.div
//                   ref={scrollRef}
//                   className="hidden lg:grid grid-cols-2 xl:grid-cols-3 gap-6"
//                   initial="hidden"
//                   animate="show"
//                   variants={{ show: { transition: { staggerChildren: 0.05 } } }}
//                 >
//                   {paginatedCards.map((card, index) => (
//                     <motion.div
//                       key={card.id}
//                       variants={{
//                         hidden: { opacity: 0, y: 20 },
//                         show: { opacity: 1, y: 0 },
//                       }}
//                       transition={{ delay: index * 0.1 }}
//                     >
//                       <LoyaltyCard card={card} index={index} />
//                     </motion.div>
//                   ))}
//                 </motion.div>
//                 <div className="hidden lg:block">
//                   {totalPages > 1 && (
//                     <div className="flex justify-center mt-6 items-center space-x-2">
//                       <button
//                         onClick={() => handlePageChange(currentPage - 1)}
//                         disabled={currentPage === 1}
//                         className={`px-3 py-1 rounded-md ${
//                           currentPage === 1
//                             ? "text-gray-500 cursor-not-allowed"
//                             : "text-white"
//                         }`}
//                       >
//                         {"<"}
//                       </button>

//                       {Array.from({ length: totalPages }, (_, i) => i + 1).map(
//                         (page) => (
//                           <motion.button
//                             key={page}
//                             onClick={() => handlePageChange(page)}
//                             whileHover={{ scale: 1.05 }}
//                             whileTap={{ scale: 0.95 }}
//                             className={`px-3 py-1 rounded-full font-semibold transition-all ${
//                               currentPage === page
//                                 ? "text-redlight border border-redlight"
//                                 : "text-white"
//                             }`}
//                           >
//                             {page}
//                           </motion.button>
//                         )
//                       )}

//                       <button
//                         onClick={() => handlePageChange(currentPage + 1)}
//                         disabled={currentPage === totalPages}
//                         className={`px-3 py-1 rounded-md ${
//                           currentPage === totalPages
//                             ? "text-gray-500 cursor-not-allowed"
//                             : "text-white"
//                         }`}
//                       >
//                         {">"}
//                       </button>
//                     </div>
//                   )}
//                 </div>
//               </div>
//             )}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// }
